import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  FaUsers,
  FaTrash,
  FaUserShield,
  FaUser,
  FaSearch,
  FaCalendar,
  FaWallet,
  FaArrowLeft,
  FaCheck,
  FaTimes
} from 'react-icons/fa';

const UserManagement = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, user: null });

  useEffect(() => {
    // Auth yüklenene kadar bekle
    if (authLoading) return;

    // Admin kontrolü
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    fetchUsers();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Arama filtresi
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get('/api/admin/users');
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`/api/admin/users/${userId}`, { role: newRole });

      // Listeyi güncelle
      setUsers(users.map(u =>
        u._id === userId ? { ...u, role: newRole } : u
      ));

      alert('Kullanıcı rolü başarıyla güncellendi!');
    } catch (error) {
      alert(error.response?.data?.message || 'Rol güncellenemedi');
    }
  };

  const handleVerificationChange = async (userId, newStatus) => {
    try {
      await axios.put(`/api/admin/users/${userId}`, { isVerified: newStatus });

      // Listeyi güncelle
      setUsers(users.map(u =>
        u._id === userId ? { ...u, isVerified: newStatus } : u
      ));

      alert(`Kullanıcı ${newStatus ? 'doğrulandı' : 'doğrulaması kaldırıldı'}!`);
    } catch (error) {
      alert(error.response?.data?.message || 'Durum güncellenemedi');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.user) return;

    try {
      await axios.delete(`/api/admin/users/${deleteModal.user._id}`);

      // Listeyi güncelle
      setUsers(users.filter(u => u._id !== deleteModal.user._id));

      alert('Kullanıcı başarıyla silindi!');
      setDeleteModal({ show: false, user: null });
    } catch (error) {
      alert(error.response?.data?.message || 'Kullanıcı silinemedi');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 transition-colors duration-300 ease-in-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Admin Paneline Dön
          </button>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Kullanıcı Yönetimi
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Tüm kullanıcıları görüntüle, düzenle ve yönet
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">Toplam Kullanıcı</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">{users.length}</h3>
              </div>
              <FaUsers className="text-blue-600 dark:text-blue-400 text-4xl" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">Admin</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {users.filter(u => u.role === 'admin').length}
                </h3>
              </div>
              <FaUserShield className="text-purple-600 dark:text-purple-400 text-4xl" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">Normal Kullanıcı</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {users.filter(u => u.role === 'user').length}
                </h3>
              </div>
              <FaUser className="text-green-600 dark:text-green-400 text-4xl" />
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Kullanıcı ara (isim veya email)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Kullanıcı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Varlıklar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Kayıt Tarihi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((u) => (
                    <tr key={u._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {u.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{u.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u._id, e.target.value)}
                          disabled={u._id === user._id}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${u.role === 'admin'
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                            : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            } ${u._id === user._id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <FaWallet className="mr-2 text-yellow-600 dark:text-yellow-400" />
                          {u.assetCount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <FaCalendar className="mr-2 text-gray-400" />
                          {formatDate(u.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleVerificationChange(u._id, !u.isVerified)}
                          disabled={u._id === user._id}
                          className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${u.isVerified
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                            : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-800'
                            } ${u._id === user._id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          title={u.isVerified ? 'Doğrulamayı kaldır' : 'Doğrula'}
                        >
                          {u.isVerified ? (
                            <><FaCheck className="mr-1" /> Doğrulanmış</>
                          ) : (
                            <><FaTimes className="mr-1" /> Doğrulanmamış</>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setDeleteModal({ show: true, user: u })}
                          disabled={u._id === user._id}
                          className={`text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 ${u._id === user._id ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title={u._id === user._id ? 'Kendi hesabınızı silemezsiniz' : 'Kullanıcıyı sil'}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      {searchTerm ? 'Kullanıcı bulunamadı' : 'Henüz kullanıcı yok'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-6">
              <div className="bg-red-100 dark:bg-red-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="text-red-600 dark:text-red-400 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Kullanıcıyı Sil
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>{deleteModal.user?.name}</strong> kullanıcısını ve tüm varlıklarını silmek istediğinizden emin misiniz?
              </p>
              <p className="text-red-600 dark:text-red-400 text-sm mt-2 font-semibold">
                Bu işlem geri alınamaz!
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setDeleteModal({ show: false, user: null })}
                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-semibold transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteUser}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
